import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { SetValueAction } from 'ngrx-forms';
import { catchError, debounceTime, filter, map, of, switchMap } from 'rxjs';
import { AddressApiService } from '../../services/api/address.service';
import { IErrorResult } from '../../services/dto.service';
import { AddressSelector } from '../../state/address/address.selectors';
import { fromAddressActions } from '../address/address.actions';
import { AppState } from '../app.state';
import { BaseEffects } from '../base.effects';
import { CartSelector } from '../cart/cart.selectors';

@Injectable()
export class AddressEffects extends BaseEffects {
	constructor(actions$: Actions, store: Store<AppState>, private addressSelector: AddressSelector, private cartSelector: CartSelector, private addressService: AddressApiService) {
		super(actions$, store);
	}

	public onSetValueForPayer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SetValueAction.TYPE),
			filter((control: SetValueAction<string>) => control.controlId.startsWith('PAYER')),
			debounceTime(100),
			concatLatestFrom(() => this.store.select(this.cartSelector.payerForm)),
			map(([, payerForm]) => fromAddressActions.suggestPayer({ address: payerForm.value.address }))
		)
	);

	public onSetValueForReceiver$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SetValueAction.TYPE),
			filter((control: SetValueAction<string>) => control.controlId.startsWith('RECEIVER')),
			debounceTime(100),
			concatLatestFrom(() => this.store.select(this.cartSelector.receiverForm)),
			map(([, receiverForm]) => fromAddressActions.suggestReceiver({ address: receiverForm.value.address }))
		)
	);

	public onSuggestPayer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromAddressActions.suggestPayer),
			concatLatestFrom(() => this.store.select(this.addressSelector.paging)),
			debounceTime(800),
			switchMap(([{ address }, paging]) =>
				this.addressService.suggest(address, paging).pipe(
					map(response => fromAddressActions.suggestedPayer({ suggestedList: response.data.filter(a => JSON.stringify(a) != JSON.stringify(address)) })),
					catchError((response: IErrorResult) => of(fromAddressActions.failed({ message: response.error?.error || response.error?.message })))
				)
			)
		)
	);

	public onSuggestReceiver$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromAddressActions.suggestReceiver),
			concatLatestFrom(() => this.store.select(this.addressSelector.paging)),
			debounceTime(800),
			switchMap(([{ address }, paging]) =>
				this.addressService.suggest(address, paging).pipe(
					map(response => fromAddressActions.suggestedReceiver({ suggestedList: response.data.filter(a => JSON.stringify(a) != JSON.stringify(address)) })),
					catchError((response: IErrorResult) => of(fromAddressActions.failed({ message: response.error?.error || response.error?.message })))
				)
			)
		)
	);

	public onSuggestedPayer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromAddressActions.suggestedPayer),
			filter(({ suggestedList }) => suggestedList.length == 1),
			map(({ suggestedList }) => suggestedList[0]),
			concatLatestFrom(() => this.store.select(this.cartSelector.payerForm)),
			switchMap(([suggestion, payerForm]) => {
				const result: Action[] = [];

				if (!payerForm.value.address.city && suggestion.city) {
					result.push(new SetValueAction('PAYER.address.city', suggestion.city));
				}

				return result;
			})
		)
	);

	public onSuggestedReceiver$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromAddressActions.suggestedReceiver),
			filter(({ suggestedList }) => suggestedList.length == 1),
			map(({ suggestedList }) => suggestedList[0]),
			concatLatestFrom(() => this.store.select(this.cartSelector.receiverForm)),
			switchMap(([suggestion, receiverForm]) => {
				const result: Action[] = [];

				if (!receiverForm.value.address.city && suggestion.city) {
					result.push(new SetValueAction('RECEIVER.address.city', suggestion.city));
				}

				return result;
			})
		)
	);
}
